<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── SEARCH ── -->
      <div class="search-wrap">
        <div class="search-box">
          <span class="search-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input
            v-model="searchText"
            class="search-input"
            type="text"
            placeholder="Search conversations…"
            @input="filterFriends"
          />
          <div class="search-bar-line"></div>
          <button v-if="searchText" class="search-clear" @click="searchText = ''; filterFriends()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Correspondence</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Your <em>Messages</em></h2>

      <!-- ── LOADING ── -->
      <div v-if="isLoading" class="loading-row">
        <div class="loading-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="loading-text">Loading your conversations…</p>
      </div>

      <!-- ── EMPTY ── -->
      <div v-else-if="filteredFriends.length === 0" class="empty-state">
        <div class="empty-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="empty-title">No conversations yet</p>
        <p class="empty-sub">Your connections will appear here.</p>
      </div>

      <!-- ── CARDS GRID ── -->
      <div v-else class="cards-grid">
        <div
          v-for="friend in filteredFriends"
          :key="friend.id"
          class="friend-card"
          @click="startConversation(friend)"
        >
          <img
            v-if="friend.image && friend.image !== defaultImage"
            :src="friend.image"
            :alt="friend.fullName"
            class="card-photo"
          />
          <div v-else class="card-photo-placeholder">
            {{ (friend.fullName || '?').charAt(0) }}
          </div>

          <div class="card-body">
            <p class="card-name">{{ friend.fullName }}</p>
            <p class="card-last">
              <span v-if="messagesMap[friend.id]?.length && messagesMap[friend.id].slice(-1)[0].sharedPost">
                📍 Shared {{ messagesMap[friend.id].slice(-1)[0].sharedPost.businessName }}
              </span>
              <span v-else-if="messagesMap[friend.id]?.length">
                {{ messagesMap[friend.id].slice(-1)[0].text }}
              </span>
              <span v-else class="card-last-empty">No messages yet</span>
            </p>
          </div>

          <div class="card-footer">
            <span
              v-if="unreadMessagesCount[friend.id] > 0"
              class="unread-badge"
            >{{ unreadMessagesCount[friend.id] }}</span>
            <span v-else style="width: 1px;"></span>

            <button class="chat-btn" @click.stop="startConversation(friend)">
              <div class="btn-tick"></div>
              <span>Chat</span>
              <div class="btn-tick"></div>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item active" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>
  </div>
</template>

<script>
import {
  collection, getDocs, doc, getDoc,
  onSnapshot, query, where, updateDoc,
} from "firebase/firestore";
import { db, auth } from "@/firebase";

export default {
  name: "MessagesView",

  data() {
    return {
      searchText: "",
      friends: [],
      filteredFriends: [],
      defaultImage: "@/assets/default-user.png",
      messagesMap: {},
      unreadMessagesCount: {},
      isLoading: true,
      receivedMessagesRaw: [],
      sentMessagesRaw: [],
      receivedListenerUnsub: null,
      sentListenerUnsub: null,
    };
  },

  beforeUnmount() {
    if (this.receivedListenerUnsub) this.receivedListenerUnsub();
    if (this.sentListenerUnsub) this.sentListenerUnsub();
  },

  async mounted() {
    await this.fetchFriends();
    this.listenToMessages();
    this.isLoading = false;
  },

  methods: {
    async fetchFriends() {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const connectionsRef = collection(db, "user", currentUser.uid, "connections");
        const connectionsSnapshot = await getDocs(connectionsRef);
        const connectionIds = connectionsSnapshot.docs.map((d) => d.id);

        const usersRef = collection(db, "user");
        const connectedUserDocs = await Promise.all(
          connectionIds.map((id) => getDoc(doc(usersRef, id)))
        );
        const connectedUsers = connectedUserDocs
          .filter((snap) => snap.exists())
          .map((snap) => ({ id: snap.id, ...snap.data() }));

        const allUsersMap = new Map();
        connectedUsers.forEach((u) => allUsersMap.set(u.id, u));

        this.friends = Array.from(allUsersMap.values());
        this.filterFriends();
      } catch (error) {
        console.error("Error fetching friends:", error);
      }
    },

    filterFriends() {
      const lower = this.searchText.toLowerCase();
      this.filteredFriends = this.friends.filter((f) =>
        f.fullName?.toLowerCase().includes(lower)
      );
    },

    async startConversation(friend) {
      await this.markMessagesAsRead(friend.id);
      this.$router.push({ name: "ChatView", params: { friendId: friend.id } });
    },

    listenToMessages() {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const receivedQ = query(
        collection(db, "messages"),
        where("receiverUid", "==", currentUser.uid)
      );
      const sentQ = query(
        collection(db, "messages"),
        where("senderId", "==", currentUser.uid)
      );

      const rebuild = () => {
        const messages = {};
        const unreadCount = {};

        this.receivedMessagesRaw.forEach((msg) => {
          const otherId = msg.senderId;
          if (!otherId) return;
          if (!messages[otherId]) messages[otherId] = [];
          messages[otherId].push(msg);
          if (!msg.read) {
            if (!unreadCount[otherId]) unreadCount[otherId] = 0;
            unreadCount[otherId]++;
          }
        });

        this.sentMessagesRaw.forEach((msg) => {
          const otherId = msg.receiverUid;
          if (!otherId) return;
          if (!messages[otherId]) messages[otherId] = [];
          messages[otherId].push(msg);
        });

        Object.keys(messages).forEach((id) => {
          messages[id].sort((a, b) => (a.timestamp?.toMillis?.() || 0) - (b.timestamp?.toMillis?.() || 0));
        });

        this.messagesMap = messages;
        this.unreadMessagesCount = unreadCount;
      };

      this.receivedListenerUnsub = onSnapshot(receivedQ, (snapshot) => {
        this.receivedMessagesRaw = snapshot.docs.map((d) => d.data());
        rebuild();
      });
      this.sentListenerUnsub = onSnapshot(sentQ, (snapshot) => {
        this.sentMessagesRaw = snapshot.docs.map((d) => d.data());
        rebuild();
      });
    },

    async markMessagesAsRead(friendId) {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const q = query(
        collection(db, "messages"),
        where("senderId", "==", friendId),
        where("receiverUid", "==", currentUser.uid),
        where("read", "==", false)
      );
      const snapshot = await getDocs(q);
      snapshot.forEach(async (docSnap) => {
        await updateDoc(doc(db, "messages", docSnap.id), { read: true });
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 1100px; margin: 0 auto; padding: 52px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── SEARCH ── */
.search-wrap { display: flex; justify-content: center; margin-bottom: 56px; }
.search-box { position: relative; width: 100%; max-width: 560px; }
.search-icon { position: absolute; left: 0; top: 50%; transform: translateY(-50%); color: rgba(210,175,100,0.4); pointer-events: none; display: flex; }
.search-icon svg { width: 18px; height: 18px; }
.search-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid rgba(237,232,223,0.15); padding: 14px 36px 14px 32px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.3s; letter-spacing: 0.3px; }
.search-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.search-input:focus { border-bottom-color: rgba(210,175,100,0.4); }
.search-bar-line { position: absolute; bottom: 0; left: 0; height: 1px; width: 0; background: linear-gradient(90deg, rgba(210,175,100,0.8), rgba(210,175,100,0.2)); transition: width 0.5s cubic-bezier(0.25,0.46,0.45,0.94); pointer-events: none; }
.search-input:focus ~ .search-bar-line { width: 100%; }
.search-clear { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.25); display: flex; align-items: center; transition: color 0.2s; padding: 4px; }
.search-clear:hover { color: rgba(210,175,100,0.6); }
.search-clear svg { width: 14px; height: 14px; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 32px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }

/* ── LOADING / EMPTY ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 80px 0; }
.loading-seal, .empty-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 0; border: 1px solid rgba(210,175,100,0.08); }
.empty-title { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(237,232,223,0.3); margin-bottom: 10px; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── CARDS GRID ── */
.cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.friend-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); cursor: pointer; transition: border-color 0.3s; position: relative; overflow: hidden; }
.friend-card::before, .friend-card::after { content: ''; position: absolute; width: 14px; height: 14px; z-index: 1; }
.friend-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.friend-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.friend-card:hover { border-color: rgba(210,175,100,0.3); }
.friend-card:hover .card-photo { filter: brightness(1.06); }

.card-photo { width: 100%; height: 200px; object-fit: cover; object-position: top center; display: block; filter: brightness(0.88) saturate(0.7); transition: filter 0.4s; }
.card-photo-placeholder { width: 100%; height: 200px; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 60px; font-weight: 300; color: rgba(210,175,100,0.15); }

.card-body { padding: 18px 20px 0; }
.card-name { font-family: 'Cormorant', serif; font-size: 20px; font-weight: 400; color: #edeae2; letter-spacing: -0.2px; margin-bottom: 5px; line-height: 1.1; }
.card-last { font-size: 10px; font-weight: 400; letter-spacing: 0.3px; color: rgba(237,232,223,0.3); margin-bottom: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-style: italic; line-height: 1.4; }
.card-last-empty { color: rgba(237,232,223,0.15); }

.card-footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(210,175,100,0.08); padding: 13px 18px; margin-top: 16px; }
.unread-badge { background: rgba(160,55,55,0.8); color: rgba(255,210,210,0.9); font-size: 9px; font-weight: 600; letter-spacing: 1px; padding: 3px 8px; min-width: 22px; text-align: center; }
.chat-btn { display: flex; align-items: center; gap: 9px; font-size: 9px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(210,175,100,0.55); background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; transition: color 0.2s; padding: 0; position: relative; overflow: hidden; }
.chat-btn:hover { color: rgba(210,175,100,0.9); }
.btn-tick { width: 14px; height: 1px; background: rgba(210,175,100,0.3); flex-shrink: 0; transition: width 0.3s; }
.friend-card:hover .btn-tick { width: 20px; }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) { .cards-grid { grid-template-columns: repeat(2, 1fr); } .page { padding: 40px 28px 120px; } }
@media (max-width: 600px) { .cards-grid { grid-template-columns: 1fr; } .page { padding: 32px 20px 120px; } .frame, .frame-inner { inset: 12px; } .section-title { font-size: 32px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>